import React, { ChangeEvent, useCallback, useMemo, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, IconButton, LinearProgress, Popover, Typography } from "@mui/material";

import "./ChangeLogoModal.styles.scss";
import { Loading } from "components";
import { ApiServiceParams, useDealActions, useDealIdSelector, useDealLogoSelec, useLogoToSaveSelect } from "store";
import { getPercentage } from "utils/files";
import { useActionHook } from "hooks";
import { AxiosProgressEvent } from "axios";

interface ChangeLogoProps {
  isChangeLogo: boolean;
  close: () => void;
}

export const ChangeLogoModal: React.FC<ChangeLogoProps> = ({ isChangeLogo, close }) => {
  const { uploadLogoAction, saveLogoAction, deleteLogoAction } = useDealActions();
  const logo = useDealLogoSelec();
  const dealId = useDealIdSelector();
  const logoToSave = useLogoToSaveSelect();

  const [progress, setProgress] = useState(0);
  const [isSpinner, setIsSpinner] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [controller, setController] = useState<AbortController | null>(null);

  const { action, loading } = useActionHook(uploadLogoAction);

  const isLogoCanBeSaved: boolean = useMemo(() => {
    return Boolean(logoToSave && logoToSave.file_url && logoToSave.state === "verified");
  }, [logoToSave]);

  const inputRef = useRef<HTMLInputElement>(null);

  const onUploadProgress: ApiServiceParams["onUploadProgress"] = useCallback((progressEvent: AxiosProgressEvent) => {
    const { total, loaded } = progressEvent;
    if (!total) return;
    setProgress(getPercentage(loaded, total));
  }, []);

  const uploadFile = useCallback(
    async (file: File) => {
      try {
        if (!dealId) return;
        const newController = new AbortController();
        setController(newController);

        setIsSpinner(true);
        await action({
          dealId,
          onUploadProgress,
          file,
          signal: newController?.signal
        });
      } catch (error) {
        throw error;
      } finally {
        setController(null);
      }
    },
    [dealId]
  );

  const handleClose = () => {
    close();
  };

  const onFileChoose = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (event.target.files?.length) {
        const file = event.target.files[0];
        uploadFile(file);
      }
    },
    [uploadFile]
  );

  const onSave = useCallback(async () => {
    try {
      if (isLogoCanBeSaved && logoToSave && dealId) {
        setIsSpinner(true);
        await saveLogoAction({ dealId, fileId: logoToSave.id });
      }
      if (isDeleting && logo) {
        setIsSpinner(true);
        await deleteLogoAction({ logoId: logo.id, isCurrentLogo: true });
        setIsDeleting(false);
      }
    } catch (error) {
      throw error;
    } finally {
      setIsSpinner(false);
      setProgress(0);
      handleClose();
    }
  }, [isLogoCanBeSaved, logoToSave, dealId, isDeleting, logo]);

  const onCancel = useCallback(async () => {
    try {
      if (isDeleting) {
        setIsDeleting(false);
      }
      if (controller) {
        controller.abort();
        setProgress(0);
        setIsSpinner(false);
        setController(null);
      }
      if (isLogoCanBeSaved && logoToSave) {
        await deleteLogoAction({ logoId: logoToSave.id });
      }
    } catch (error) {
      throw error;
    } finally {
      setIsSpinner(false);
      setProgress(0);
      handleClose();
    }
  }, [controller, isLogoCanBeSaved, isSpinner, isDeleting, logoToSave]);

  return (
    <Popover
      open={isChangeLogo}
      onClose={handleClose}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right"
      }}
    >
      <div className="edit-logo-modal">
        <div className="head">
          <span className="title">Edit Logo</span>
          <IconButton size="sm" onClick={handleClose}>
            <CloseIcon fontSize="md" />
          </IconButton>
        </div>
        <div className="body">
          {/* Original logo if it is */}
          {logo && !loading && !logoToSave && !isDeleting && (
            <div className="file-holder file-holder--space">
              <span className="name">{logo?.name}</span>
              <IconButton sx={{ backgroundColor: "#dae0e2" }} size="sm" onClick={() => setIsDeleting(true)}>
                <CloseIcon fontSize="md" />
              </IconButton>
            </div>
          )}
          {!(loading || logoToSave) ? (
            <div className="input-block">
              <div
                onClick={() => {
                  if (inputRef.current) {
                    inputRef.current.click();
                  }
                }}
                className="trigger"
              >
                Browse Image
                <input ref={inputRef} onChange={onFileChoose} className="input" type="file" />
              </div>
            </div>
          ) : (
            <>
              <div className="file-holder">
                {loading ? (
                  <Loading loading={loading} />
                ) : (
                  <>
                    <span className="name">{logoToSave?.name}</span>
                    <IconButton
                      sx={{ backgroundColor: "#dae0e2" }}
                      size="sm"
                      disabled={!isLogoCanBeSaved}
                      onClick={onCancel}
                    >
                      <CloseIcon fontSize="md" />
                    </IconButton>
                  </>
                )}
              </div>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box sx={{ width: "100%", mr: 1 }}>
                  <LinearProgress variant="determinate" value={progress} />
                </Box>
                <Box sx={{ minWidth: 35 }}>
                  <Typography variant="body2" color="text.secondary">{`${Math.round(progress)}%`}</Typography>
                </Box>
              </Box>
            </>
          )}
        </div>
        <div className="footer">
          <Button variant="outlined" onClick={onCancel}>
            Cancel
          </Button>
          <Button disabled={!(isLogoCanBeSaved || isDeleting)} onClick={onSave} variant="contained">
            Save
          </Button>
        </div>
      </div>
    </Popover>
  );
};
